import { Modal, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import Loading from '@/components/Loading'
import { CONTRACT_REVERSE_ADDRESS, FREE_MIN_BALANCE, ID_BOT } from '@/constants/blockchain'
import { useErrorMessages } from '@/constants/content'
import L1Reverse from '@/contracts/ReverseRegistrar'
import { useChainFunctions, useChainValues } from '@/hooks/useChain'
import useL1NS from '@/hooks/useL1NS'
import useLocalStorage from '@/hooks/useLocalStorage'
import { l1IdSubnetWagmiConfig } from '@/plugins/auth/config'
import { GetL1Names } from '@/schemas/identity.schema'
import { NameInfo } from '@/utils/identity'
import { ErrorMessageDisplay, post, UserIdentity } from '@/utils/utils'

interface ManageUsernamesModalProps {
  close: () => void
  data: GetL1Names | undefined
  profileAddress: string
  setRefetchUsername: () => void
}

interface UsernameWithInfo extends NameInfo {
  createdAt: string
}

type RenewalStatus = 'active' | 'grace-period' | 'expired'

const ManageUsernamesModal: React.FC<ManageUsernamesModalProps> = ({
  close,
  data,
  profileAddress,
  setRefetchUsername,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const { getL1nsName, getNameInfoFromTokenId } = useL1NS()
  const { writeContract } = useChainFunctions(l1IdSubnetWagmiConfig)
  const { balance } = useChainValues(l1IdSubnetWagmiConfig.id)
  const errorMessages = useErrorMessages()
  const [userIdentity, setUserIdentity] = useLocalStorage<UserIdentity>(
    `userIdentity-${profileAddress}`
  )

  // Error Modal
  const [openedErrorModal, { open: openErrorModal, close: closeErrorModal }] = useDisclosure(false)
  const [errorMessage, setErrorMessage] = useState<ErrorMessageDisplay>({
    message: '',
    details: '',
    report: true,
  })

  // State
  const [loading, setLoading] = useState<boolean>(true)
  const [activeUsername, setActiveUsername] = useState<string | undefined>(undefined)
  const [usernamesWithInfo, setUsernamesWithInfo] = useState<UsernameWithInfo[]>([])
  const [selectedUsername, setSelectedUsername] = useState<string | undefined>(undefined)

  // Helper function to determine renewal status
  const getRenewalStatus = (nameInfo: NameInfo): RenewalStatus => {
    if (nameInfo.kind === 'free') return 'active'

    const currentTime = BigInt(Math.floor(Date.now() / 1000))
    const expiryTime = nameInfo.expiry

    // If current time is past expiry
    if (currentTime > expiryTime) {
      return nameInfo.available ? 'expired' : 'grace-period'
    }

    return 'active'
  }

  // Helper function to get visual styling based on renewal status
  const getVisualStyling = (nameInfo: NameInfo, isActive: boolean, isSelected: boolean) => {
    const renewalStatus = getRenewalStatus(nameInfo)

    if (isActive) {
      // Check if active username needs renewal or repurchase
      if (renewalStatus === 'grace-period' || renewalStatus === 'expired') {
        return {
          borderColor:
            renewalStatus === 'grace-period'
              ? theme.colors.barChartYellow[0]
              : theme.colors.notification[0],
          backgroundColor:
            renewalStatus === 'grace-period'
              ? `${theme.colors.barChartYellow[0]}10`
              : `${theme.colors.notification[0]}10`,
          cursor: 'pointer' as const,
        }
      }

      return {
        borderColor: theme.colors.l1Primary[0],
        backgroundColor: `${theme.colors.l1Primary[0]}10`,
        cursor: 'default' as const,
      }
    }

    if (isSelected) {
      return {
        borderColor: theme.colors.l1Secondary[0],
        backgroundColor: `${theme.colors.l1Primary[0]}05`,
        cursor: 'pointer' as const,
      }
    }

    // Handle renewal status visual cues
    switch (renewalStatus) {
      case 'grace-period':
        return {
          borderColor: theme.colors.barChartYellow[0],
          backgroundColor: `${theme.colors.barChartYellow[0]}10`,
          cursor: 'pointer' as const,
        }
      case 'expired':
        return {
          borderColor: theme.colors.notification[0],
          backgroundColor: `${theme.colors.notification[0]}10`,
          cursor: 'pointer' as const,
        }
      default:
        return {
          borderColor: theme.colors.popUpOutline[0],
          backgroundColor: theme.colors.background[0],
          cursor: 'pointer' as const,
        }
    }
  }

  // Helper function to get renewal status text
  const getRenewalStatusText = (nameInfo: NameInfo): string | null => {
    const renewalStatus = getRenewalStatus(nameInfo)

    switch (renewalStatus) {
      case 'grace-period':
        return t('components.profile.modal.needsRenewal', 'Needs Renewal')
      case 'expired':
        return t('components.profile.modal.expired', 'Expired')
      default:
        return null
    }
  }

  // Fetch name info for each tokenID
  const fetchL1Names = useCallback(async () => {
    setLoading(true)
    const activeNmae = await getL1nsName(profileAddress)
    setActiveUsername(activeNmae)
    if (!data) return

    const nameInfos = await Promise.all(
      data.balance.map(async ({ token, updatedAt }) => {
        const nameInfo = await getNameInfoFromTokenId(token.nativeId)
        return { ...nameInfo, createdAt: updatedAt }
      })
    )

    // Place active name at the top, then all the premium names, then all the free names
    const activeName = nameInfos.find(nameInfo => nameInfo.name === activeNmae)
    const premiumNames = nameInfos.filter(
      nameInfo => nameInfo.kind === 'premium' && nameInfo.name !== activeNmae
    )
    const freeNames = nameInfos
      .filter(nameInfo => nameInfo.kind === 'free')
      .filter(nameInfo => nameInfo.name !== activeNmae)
    if (activeName) {
      setUsernamesWithInfo([activeName, ...premiumNames, ...freeNames])
    } else {
      setUsernamesWithInfo([...premiumNames, ...freeNames])
    }
    setLoading(false)
  }, [data, profileAddress, getNameInfoFromTokenId, getL1nsName])

  useEffect(() => {
    fetchL1Names()
  }, [data, profileAddress])

  const formatExpiryDate = (expiry: bigint): string => {
    const expiryDate = new Date(Number(expiry) * 1000)
    return expiryDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleUsernameSelect = (username: string) => {
    // Allow selecting active username if it needs renewal or repurchase
    if (username === activeUsername) {
      const activeNameInfo = usernamesWithInfo.find(nameInfo => nameInfo.name === username)
      if (activeNameInfo) {
        const renewalStatus = getRenewalStatus(activeNameInfo)
        if (renewalStatus === 'active') {
          // Don't allow selecting active username if it's in good standing
          return
        }
      }
    }

    setSelectedUsername(selectedUsername === username ? undefined : username)
  }

  const handlePurchaseUsername = useCallback(() => {
    navigate('/premiumusername', {
      state: {
        pathname: '/profile',
      },
    })
  }, [activeUsername, navigate])

  const handleSwitchUsername = async () => {
    if (!selectedUsername || !profileAddress) return

    // Get L1ID tokens from faucet if needed
    if (parseFloat(balance.fullValue) < FREE_MIN_BALANCE) {
      // Post Faucet to request L1ID tokens
      try {
        const retval = await post(`${ID_BOT}/faucet`, JSON.stringify({ address: profileAddress }))
        if (retval?.txHash === '') {
          // assume the time string is of the time "23h50m15s"
          const time = (retval.ttl as string)
            .replace('s', ' seconds')
            .replace('h', ' hours ')
            .replace('m', ' minutes ')

          setErrorMessage({
            message: errorMessages.idBotRateLimit(time),
            details: retval.error,
            report: false,
          })
          openErrorModal()
          return
        }
      } catch (err: any) {
        setErrorMessage({
          message: errorMessages.idBotConnection,
          details: `${t(
            'components.identity.errorMessageFree',
            'The Lamina1 ID Bot is being used to request tokens from the faucet. Until you have enough balance to pay the transaction fee cost, you cannot set a username. Please try again later.'
          )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
            error: err.message,
          })}`,
        })
        openErrorModal()
        return
      }
    }

    try {
      // Set username on reverse registrar
      await writeContract({
        address: CONTRACT_REVERSE_ADDRESS,
        abi: L1Reverse,
        functionName: 'setName',
        args: [selectedUsername],
      })

      // Update the active username
      setActiveUsername(selectedUsername)
      setSelectedUsername(undefined)
      if (userIdentity) {
        setUserIdentity({
          ...userIdentity,
          username: selectedUsername,
        })
      }
      setRefetchUsername()

      // Close the modal or show success message
      close()
    } catch (err2: any) {
      setErrorMessage({
        message: errorMessages.idBotConnection,
        details: err2.message,
        report: false,
      })
      openErrorModal()
    }
  }

  const handleRenewUsername = useCallback(() => {
    if (!selectedUsername) return

    navigate('/premiumusername', {
      state: {
        username: selectedUsername,
        pathname: '/profile',
        isRenewal: true,
      },
    })
  }, [selectedUsername, navigate])

  const handleRepurchaseUsername = useCallback(() => {
    if (!selectedUsername) return

    navigate('/premiumusername', {
      state: {
        username: selectedUsername,
        pathname: '/profile',
      },
    })
  }, [selectedUsername, navigate])

  const getSelectedUsernameAction = () => {
    if (!selectedUsername) return null

    const selectedNameInfo = usernamesWithInfo.find(nameInfo => nameInfo.name === selectedUsername)
    if (!selectedNameInfo) return null

    const renewalStatus = getRenewalStatus(selectedNameInfo)

    switch (renewalStatus) {
      case 'active':
        return {
          text: t('components.profile.modal.switchToUsername', 'Switch to @{{username}}', {
            username: selectedUsername,
          }),
          action: handleSwitchUsername,
          disabled: false,
        }
      case 'grace-period':
        return {
          text: t('components.profile.modal.renewUsername', 'Renew @{{username}}', {
            username: selectedUsername,
          }),
          action: handleRenewUsername,
          disabled: false,
        }
      case 'expired':
        return {
          text: t('components.profile.modal.repurchaseUsername', 'Repurchase @{{username}}', {
            username: selectedUsername,
          }),
          action: handleRepurchaseUsername,
          disabled: false,
        }
      default:
        return null
    }
  }

  const selectedUsernameAction = getSelectedUsernameAction()

  return (
    <Stack style={{ gap: '20px' }}>
      {loading ? (
        <Loading />
      ) : usernamesWithInfo.length === 0 ? (
        <Text style={{ color: theme.colors.paragraph[0], textAlign: 'center' }}>
          {t('components.profile.modal.noUsernames', 'No usernames found')}
        </Text>
      ) : (
        <Stack style={{ gap: '15px' }}>
          <Stack
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              paddingRight: '8px',
            }}
          >
            <Text style={{ color: theme.colors.paragraph[0], fontSize: '14px' }}>
              You own {usernamesWithInfo.filter(nameInfo => nameInfo.kind === 'premium').length}{' '}
              premium usernames and{' '}
              {usernamesWithInfo.filter(nameInfo => nameInfo.kind === 'free').length} free
              usernames.{' '}
              {activeUsername === '' &&
                'You currently have no active username, please select one from the list below.'}
            </Text>
            <Stack style={{ gap: '15px' }}>
              {usernamesWithInfo.map(nameInfo => {
                const isActive = nameInfo.name === activeUsername
                const isSelected = nameInfo.name === selectedUsername
                const visualStyling = getVisualStyling(nameInfo, isActive, isSelected)
                const renewalStatusText = getRenewalStatusText(nameInfo)

                return (
                  <Stack
                    key={nameInfo.name}
                    style={{
                      padding: '15px',
                      border: `1px solid ${visualStyling.borderColor}`,
                      borderRadius: '8px',
                      backgroundColor: visualStyling.backgroundColor,
                      cursor: visualStyling.cursor,
                    }}
                    onClick={() => handleUsernameSelect(nameInfo.name)}
                  >
                    <Stack
                      style={{
                        minWidth: '400px',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Title order={4} style={{ color: theme.colors.title[0] }}>
                        @{nameInfo.name}
                      </Title>
                      <Stack style={{ flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
                        {isActive && (
                          <Text
                            style={{
                              color: theme.colors.l1Primary[0],
                              fontSize: '12px',
                              fontWeight: 'bold',
                              backgroundColor: `${theme.colors.l1Primary[0]}20`,
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {t('components.profile.modal.active', 'Active')}
                          </Text>
                        )}
                        {isSelected && (
                          <Text
                            style={{
                              color: theme.colors.l1Primary[0],
                              fontSize: '12px',
                              fontWeight: 'bold',
                              backgroundColor: `${theme.colors.l1Secondary[0]}20`,
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {t('components.profile.modal.selected', 'Selected')}
                          </Text>
                        )}
                        {renewalStatusText && (
                          <Text
                            style={{
                              color:
                                getRenewalStatus(nameInfo) === 'grace-period'
                                  ? theme.colors.barChartYellow[0]
                                  : theme.colors.notification[0],
                              fontSize: '12px',
                              fontWeight: 'bold',
                              backgroundColor:
                                getRenewalStatus(nameInfo) === 'grace-period'
                                  ? `${theme.colors.barChartYellow[0]}20`
                                  : `${theme.colors.notification[0]}20`,
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {renewalStatusText}
                          </Text>
                        )}
                        <Text
                          style={{
                            color:
                              nameInfo.kind === 'premium'
                                ? theme.colors.l1Primary[0]
                                : theme.colors.paragraph[0],
                            fontSize: '14px',
                            fontWeight: nameInfo.kind === 'premium' ? 'bold' : 'normal',
                          }}
                        >
                          {nameInfo.kind === 'premium' ? 'Premium' : 'Free'}
                        </Text>
                      </Stack>
                    </Stack>
                    <Stack
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Text style={{ color: theme.colors.paragraph[0], fontSize: '14px' }}>
                        {t('components.profile.modal.acquiredDate', 'Acquired:')}{' '}
                        {new Date(nameInfo.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Text>
                      {nameInfo.kind === 'premium' && (
                        <Text style={{ color: theme.colors.paragraph[0], fontSize: '14px' }}>
                          {t('components.profile.modal.expiryDate', 'Expires:')}{' '}
                          {formatExpiryDate(nameInfo.expiry)}
                        </Text>
                      )}
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
          </Stack>
          <Stack
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '20px',
            }}
          >
            <RootButton onClick={handlePurchaseUsername}>
              {t('components.profile.modal.purchaseUsername', 'Purchase Username')}
            </RootButton>
            {usernamesWithInfo.length > 0 && (
              <RootButton
                onClick={selectedUsernameAction?.action}
                disabled={!selectedUsernameAction}
              >
                {selectedUsernameAction?.text ||
                  t('components.profile.modal.selectUsername', 'Select a username')}
              </RootButton>
            )}
          </Stack>
        </Stack>
      )}
      <Modal
        opened={openedErrorModal}
        onClose={closeErrorModal}
        size="lg"
        lockScroll={false}
        style={{ header: { margin: '0 1.6px' } }}
      >
        <ErrorMessage
          message={errorMessage.message}
          details={errorMessage.details}
          report={errorMessage.report}
        />
      </Modal>
    </Stack>
  )
}

export default ManageUsernamesModal
